import React, { useState, useEffect } from "react";
import {
  Activity,
  CalendarClock,
  ClipboardList,
  HelpCircle,
  ListChecks,
  PanelRightClose,
  Shield,
  Star,
  Clock,
  AlertCircle,
  CheckCircle,
  BarChart3,
  TrendingUp,
  Upload,
  Award,
  FileText,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { getFirestore, collection, query, where, onSnapshot, orderBy, doc, getDoc, getDocs } from "firebase/firestore";
import { firebaseApp } from "../../firebase";
import RaiseTicketModal from "../Support/RaiseTicketModal";
import { TicketType } from "../../types/ticket";
import { useVerificationStatus } from "../../hooks/useVerificationStatus";

interface UtilityPanelProps {
  isOpen: boolean;
  userType: "jobseeker" | "hr" | "admin";
  onClose: () => void;
}

const UtilityPanel: React.FC<UtilityPanelProps> = ({
  isOpen,
  userType,
  onClose,
}) => {
  const { user } = useAuth();
  const db = getFirestore(firebaseApp);
  const { isVerified, verificationScore } = useVerificationStatus();

  // State for Job Seeker journey data
  const [jobSeekerData, setJobSeekerData] = useState<{
    resumeUploaded: boolean;
    skillsCount: number;
    testsCompleted: number;
    interviewStatus: string;
    loading: boolean;
  }>({
    resumeUploaded: false,
    skillsCount: 0,
    testsCompleted: 0,
    interviewStatus: 'not_started',
    loading: true,
  });

  // State for HR real-time data
  const [stats, setStats] = useState({
    totalApplicants: 0,
    testsCompletedToday: 0,
    avgScore: 0,
    pendingReviews: 0,
    shortlistedCount: 0,
    highScorers: 0,
  });

  const [topCandidates, setTopCandidates] = useState<any[]>([]);
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [actionItems, setActionItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // State for ticket modal
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [ticketType, setTicketType] = useState<TicketType>('general');

  // Fetch job seeker profile and interview session data
  useEffect(() => {
    if (!user || userType !== 'jobseeker') return;

    const fetchJobSeekerData = async () => {
      try {
        // Fetch user profile from both collections
        const profileDoc = await getDoc(doc(db, 'userProfiles', user.id));
        const jobseekerDoc = await getDoc(doc(db, 'jobseekers', user.id));

        let resumeUploaded = false;
        let skillsCount = 0;

        if (profileDoc.exists()) {
          const profileData = profileDoc.data();
          resumeUploaded = profileData.resumeUploaded || false;
          skillsCount = profileData.resumeData?.professionalInfo?.skills?.length || 0;
        }

        if (jobseekerDoc.exists() && !resumeUploaded) {
          const jsData = jobseekerDoc.data();
          resumeUploaded = jsData.resumeUploaded || false;
        }

        // Fetch interview sessions for status
        const sessionsQuery = query(
          collection(db, 'interviewSessions'),
          where('candidateId', '==', user.id)
        );

        const sessionsSnapshot = await getDocs(sessionsQuery);
        const sessions: any[] = sessionsSnapshot.docs.map(docSnap => ({
          id: docSnap.id,
          ...docSnap.data()
        }));

        // Sort by createdAt in JavaScript (to avoid needing a Firebase index)
        const sortedSessions = sessions.sort((a: any, b: any) => {
          const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
          const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
          return dateB.getTime() - dateA.getTime(); // Descending order
        });

        // Get the latest session status
        const latestSession: any = sortedSessions[0];
        const interviewStatus = latestSession?.status || 'not_started';

        setJobSeekerData({
          resumeUploaded,
          skillsCount,
          testsCompleted: 0,
          interviewStatus,
          loading: false,
        });
      } catch (error) {
        setJobSeekerData(prev => ({ ...prev, loading: false }));
      }
    };

    fetchJobSeekerData();
  }, [user, db, userType]);

  // Fetch real-time data for HR users
  useEffect(() => {
    if (!user || userType !== 'hr') return;

    let unsubscribeResults: (() => void) | undefined;
    let unsubscribeShortlisted: (() => void) | undefined;

    const setupListeners = async () => {
      try {
        // Query all test results for this HR
        const resultsQuery = query(
          collection(db, 'jobseekerResults'),
          where('hrId', '==', user.id),
          orderBy('completedAt', 'desc')
        );

        // Query shortlisted candidates
        const shortlistedQuery = query(
          collection(db, 'hrShortlisted'),
          where('hrId', '==', user.id)
        );

        unsubscribeResults = onSnapshot(resultsQuery, (snapshot) => {
          const results = snapshot.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          }));
          // Calculate stats
          const totalApplicants = results.length;

          // Tests completed today
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const testsToday = results.filter((r: any) => {
            const completedDate = r.completedAt?.toDate
              ? r.completedAt.toDate()
              : new Date(r.completedAt);
            return completedDate >= today;
          }).length;

          // Average score
          const avgScore =
            results.length > 0
              ? Math.round(
                  results.reduce((sum: number, r: any) => sum + (r.totalScore || 0), 0) /
                    results.length
                )
              : 0;

          // Pending reviews
          const pendingReviews = results.filter(
            (r: any) =>
              r.hasCompletedInterview && !r.isShortlisted && !r.isRejected
          ).length;

          // High scorers (80%+)
          const highScorers = results.filter((r: any) => (r.totalScore || 0) >= 80).length;

          setStats({
            totalApplicants,
            testsCompletedToday: testsToday,
            avgScore,
            pendingReviews,
            shortlistedCount: 0,
            highScorers,
          });

          // Top 3 candidates by score (for right panel)
          const topThree = [...results]
            .filter((r: any) => r.totalScore && r.totalScore > 0)
            .sort((a: any, b: any) => (b.totalScore || 0) - (a.totalScore || 0))
            .slice(0, 3)
            .map((r: any) => ({
              id: r.jobSeekerId,
              name: r.jobSeekerName || 'Unknown',
              score: r.totalScore || 0,
              recommendation: r.recommendationForHiring || 'NEUTRAL',
              violations: r.proctoringViolations || 0,
            }));

          setTopCandidates(topThree);

          // Recent activity (last 3)
          const recentThree = results.slice(0, 3).map((r: any) => {
            const completedDate = r.completedAt?.toDate
              ? r.completedAt.toDate()
              : new Date(r.completedAt);
            const now = new Date();
            const hoursAgo = Math.floor(
              (now.getTime() - completedDate.getTime()) / (1000 * 60 * 60)
            );

            return {
              candidateName: r.jobSeekerName || 'Unknown',
              timeAgo: hoursAgo < 1 ? 'Just now' : hoursAgo < 24 ? `${hoursAgo}h ago` : `${Math.floor(hoursAgo / 24)}d ago`,
            };
          });

          setRecentActivity(recentThree);

          // Generate action items
          const items: any[] = [];

          if (pendingReviews > 0) {
            items.push(`${pendingReviews} candidates awaiting manual review`);
          }

          const violations = results.filter(
            (r: any) => r.proctoringViolations && r.proctoringViolations > 3
          ).length;
          if (violations > 0) {
            items.push(`${violations} candidates flagged for violations`);
          }

          if (testsToday > 0) {
            items.push(`${testsToday} tests completed today`);
          }

          setActionItems(items);
          setLoading(false);
        });

        unsubscribeShortlisted = onSnapshot(shortlistedQuery, (snapshot) => {
          setStats((prev) => ({
            ...prev,
            shortlistedCount: snapshot.docs.length,
          }));
        });
      } catch (error) {
        setLoading(false);
      }
    };

    setupListeners();

    return () => {
      if (unsubscribeResults) unsubscribeResults();
      if (unsubscribeShortlisted) unsubscribeShortlisted();
    };
  }, [user, db, userType]);

  if (!isOpen) {
    return null;
  }

  const commonHeader = (
    <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
          Context panel
        </p>
        <p className="text-sm text-neutral-700">
          Insights adapt to the screen you are on.
        </p>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="rounded-button border border-neutral-200 bg-white p-2 text-neutral-500 transition hover:bg-neutral-100"
        aria-label="Close context panel"
      >
        <PanelRightClose className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </div>
  );

  const renderJobSeekerContent = () => {
    if (jobSeekerData.loading) {
      return (
        <div className="space-y-4">
          <div className="animate-pulse space-y-3">
            <div className="h-24 bg-neutral-200 rounded"></div>
            <div className="h-24 bg-neutral-200 rounded"></div>
          </div>
        </div>
      );
    }

    // Helper function to get AI interview status display
    const getInterviewStatusDisplay = () => {
      const status = jobSeekerData.interviewStatus;

      switch (status) {
        case 'completed':
          return { color: 'bg-success', text: 'Completed' };
        case 'in_progress':
          return { color: 'bg-info', text: 'In progress' };
        case 'scheduled':
          return { color: 'bg-warning', text: 'Scheduled' };
        case 'terminated':
          return { color: 'bg-danger', text: 'Terminated' };
        case 'not_started':
        default:
          return { color: 'bg-neutral-200', text: 'Upcoming' };
      }
    };

    const interviewDisplay = getInterviewStatusDisplay();

    return (
      <div className="space-y-4">
        <section className="rounded-card border border-neutral-200 bg-white p-4 shadow-card">
          <div className="flex items-center gap-2 text-sm font-semibold text-neutral-800">
            <ListChecks className="h-4 w-4 text-info" strokeWidth={1.75} />
            Journey tracker
          </div>
          <ul className="mt-3 space-y-3 text-sm text-neutral-600">
            {/* Resume Upload Status */}
            <li className="flex items-start gap-2">
              <span className={`mt-1 h-2 w-2 rounded-full flex-shrink-0 ${jobSeekerData.resumeUploaded ? 'bg-success' : 'bg-neutral-200'}`} />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className={jobSeekerData.resumeUploaded ? 'text-neutral-900 font-medium' : 'text-neutral-500'}>
                    Resume uploaded
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${jobSeekerData.resumeUploaded ? 'bg-success/10 text-success' : 'bg-neutral-100 text-neutral-500'}`}>
                    {jobSeekerData.resumeUploaded ? 'Complete' : 'Pending'}
                  </span>
                </div>
                {jobSeekerData.resumeUploaded && jobSeekerData.skillsCount > 0 && (
                  <p className="text-xs text-neutral-500 mt-1">{jobSeekerData.skillsCount} skills extracted</p>
                )}
              </div>
            </li>

            {/* Skills Verification Status */}
            <li className="flex items-start gap-2">
              <span className={`mt-1 h-2 w-2 rounded-full flex-shrink-0 ${isVerified ? 'bg-success' : jobSeekerData.resumeUploaded ? 'bg-info' : 'bg-neutral-200'}`} />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className={isVerified ? 'text-neutral-900 font-medium' : 'text-neutral-500'}>
                    Skills verification
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    isVerified ? 'bg-success/10 text-success' :
                    jobSeekerData.resumeUploaded ? 'bg-info/10 text-info' :
                    'bg-neutral-100 text-neutral-500'
                  }`}>
                    {isVerified ? 'Verified' : jobSeekerData.resumeUploaded ? 'Ready' : 'Upload resume first'}
                  </span>
                </div>
                {isVerified && verificationScore && (
                  <p className="text-xs text-neutral-500 mt-1">Score: {verificationScore}%</p>
                )}
              </div>
            </li>

            {/* AI Interview Status */}
            <li className="flex items-start gap-2">
              <span className={`mt-1 h-2 w-2 rounded-full flex-shrink-0 ${interviewDisplay.color}`} />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className={isVerified ? 'text-neutral-900' : 'text-neutral-500'}>
                    AI interviews
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    isVerified ? 'bg-info/10 text-info' : 'bg-neutral-100 text-neutral-500'
                  }`}>
                    {isVerified ? interviewDisplay.text : 'Verify skills first'}
                  </span>
                </div>
              </div>
            </li>
          </ul>
        </section>

        <section className="rounded-card border border-neutral-200 bg-white p-4 shadow-card">
          <div className="flex items-center gap-2 text-sm font-semibold text-neutral-800">
            <HelpCircle className="h-4 w-4 text-info" strokeWidth={1.75} />
            Help - Raise Tickets
          </div>
          <p className="mt-2 text-sm text-neutral-600">
            Need assistance? Have a question? Raise a support ticket and our team will help you resolve any issues.
          </p>
          <div className="mt-3">
            <button
              type="button"
              className="w-full justify-between bg-red-800 text-white px-4 py-3 rounded-lg hover:bg-red-700 transition-colors font-medium"
              onClick={() => {
                setTicketType('general');
                setShowTicketModal(true);
              }}
            >
              General Query
            </button>
          </div>
        </section>
      </div>
    );
  };

  const renderHRContent = () => {
    if (loading) {
      return (
        <div className="space-y-4">
          <div className="animate-pulse space-y-3">
            <div className="h-20 bg-neutral-200 rounded"></div>
            <div className="h-20 bg-neutral-200 rounded"></div>
          </div>
        </div>
      );
    }

    const getRecommendationBadge = (rec: string) => {
      switch (rec) {
        case 'STRONG RECOMMEND':
          return (
            <span className="ml-2 rounded-pill bg-success/10 px-2 py-1 text-xs text-success">
              Strong
            </span>
          );
        case 'RECOMMEND':
          return (
            <span className="ml-2 rounded-pill bg-info/10 px-2 py-1 text-xs text-info">
              Recommend
            </span>
          );
        default:
          return (
            <span className="ml-2 rounded-pill bg-warning/10 px-2 py-1 text-xs text-warning">
              Watch
            </span>
          );
      }
    };

    return (
      <div className="space-y-4">
        <section className="rounded-card border border-neutral-200 bg-white p-4 shadow-card">
          <div className="flex items-center gap-2 text-sm font-semibold text-neutral-800">
            <ClipboardList className="h-4 w-4 text-info" strokeWidth={1.75} />
            Candidate triage (top 3)
          </div>
          {topCandidates.length > 0 ? (
            <ul className="mt-3 space-y-3 text-sm text-neutral-700">
              {topCandidates.map((candidate, index) => (
                <li key={candidate.id || index} className="flex items-center justify-between">
                  <span className="font-semibold text-neutral-900">
                    {candidate.name}
                    {getRecommendationBadge(candidate.recommendation)}
                  </span>
                  <span className="text-neutral-500">
                    {candidate.violations > 0
                      ? `Violations · ${candidate.violations}`
                      : `AI rec · ${candidate.score}%`}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-3 text-sm text-neutral-500">No candidates yet</p>
          )}
        </section>

        <section className="rounded-card border border-neutral-200 bg-white p-4 shadow-card">
          <div className="flex items-center gap-2 text-sm font-semibold text-neutral-800">
            <CalendarClock className="h-4 w-4 text-info" strokeWidth={1.75} />
            Upcoming checkpoints
          </div>
          <ul className="mt-3 space-y-2 text-sm text-neutral-600">
            {actionItems.length > 0 ? (
              actionItems.map((item, index) => <li key={index}>{item}</li>)
            ) : (
              <li>No pending actions</li>
            )}
          </ul>
          {stats.highScorers > 0 && (
            <button type="button" className="btn-primary mt-3 w-full">
              Review {stats.highScorers} ready candidates
            </button>
          )}
        </section>

        {recentActivity.length > 0 && (
          <section className="rounded-card border border-neutral-200 bg-white p-4 shadow-card">
            <div className="flex items-center gap-2 text-sm font-semibold text-neutral-800">
              <Clock className="h-4 w-4 text-info" strokeWidth={1.75} />
              Recent activity
            </div>
            <ul className="mt-3 space-y-2 text-sm text-neutral-600">
              {recentActivity.map((activity, index) => (
                <li key={index}>
                  {activity.candidateName} completed · {activity.timeAgo}
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="rounded-card border border-neutral-200 bg-white p-4 shadow-card">
          <div className="flex items-center gap-2 text-sm font-semibold text-neutral-800">
            <HelpCircle className="h-4 w-4 text-info" strokeWidth={1.75} />
            Help - Raise Tickets
          </div>
          <p className="mt-2 text-sm text-neutral-600">
            Need assistance? Have a question? Raise a support ticket and our team will help you resolve any issues.
          </p>
          <div className="mt-3">
            <button
              type="button"
              className="w-full bg-red-800 text-white px-4 py-3 rounded-lg hover:bg-red-700 transition-colors font-medium"
              onClick={() => {
                setTicketType('general');
                setShowTicketModal(true);
              }}
            >
              Raise Ticket
            </button>
          </div>
        </section>
      </div>
    );
  };

  const renderAdminContent = () => (
    <div className="space-y-4">
      <section className="rounded-card border border-neutral-200 bg-white p-4 shadow-card">
        <div className="flex items-center gap-2 text-sm font-semibold text-neutral-800">
          <Activity className="h-4 w-4 text-info" strokeWidth={1.75} />
          System health
        </div>
        <dl className="mt-3 space-y-2 text-sm text-neutral-600">
          <div className="flex items-center justify-between">
            <dt>API latency</dt>
            <dd className="rounded-pill bg-success/10 px-2 py-1 text-xs font-semibold text-success">
              142ms
            </dd>
          </div>
          <div className="flex items-center justify-between">
            <dt>Firestore quota</dt>
            <dd className="rounded-pill bg-warning/10 px-2 py-1 text-xs font-semibold text-warning">
              63% used
            </dd>
          </div>
          <div className="flex items-center justify-between">
            <dt>Functions errors</dt>
            <dd className="rounded-pill bg-danger/10 px-2 py-1 text-xs font-semibold text-danger">
              2 flagged
            </dd>
          </div>
        </dl>
      </section>

      <section className="rounded-card border border-neutral-200 bg-white p-4 shadow-card">
        <div className="flex items-center gap-2 text-sm font-semibold text-neutral-800">
          <Shield className="h-4 w-4 text-info" strokeWidth={1.75} />
          Compliance reminders
        </div>
        <ul className="mt-3 space-y-2 text-sm text-neutral-600">
          <li>14 badge revocations pending justification</li>
          <li>New SOC2 deliverable uploaded yesterday</li>
          <li>Retention policy reminder in 48h</li>
        </ul>
      </section>
    </div>
  );

  const renderContent = () => {
    switch (userType) {
      case "hr":
        return renderHRContent();
      case "admin":
        return renderAdminContent();
      default:
        return renderJobSeekerContent();
    }
  };

  return (
    <>
      <aside className="flex w-[320px] flex-col border-l border-neutral-200 bg-neutral-50">
        {commonHeader}
        <div className="flex-1 space-y-5 overflow-y-auto px-4 py-4">
          {renderContent()}
        </div>
      </aside>

      {/* Ticket Modal */}
      <RaiseTicketModal
        isOpen={showTicketModal}
        onClose={() => setShowTicketModal(false)}
        initialType={ticketType}
        userType={userType}
      />
    </>
  );
};

export default UtilityPanel;
